import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Screen } from "../components/Screen";
import { Ref } from "../components/Ref";
import { Reveal } from "../components/Reveal";
import { Segmented } from "../components/Segmented";
import { BloodField, type BLevel } from "../illustrations/BloodField";
import { useSnak } from "../snak/SnakContext";
const LEVELS: Record<BLevel, {
  name: string;
  definition: string;
  note: string;
}> = {
  B0: {
    name: "Absence of significant blood involvement",
    definition: "≤5% of peripheral blood lymphocytes are atypical (Sézary) cells.",
    note: "No significant circulating tumour burden. Skin, lymph nodes and viscera are staged separately (T, N, M)."
  },
  B1: {
    name: "Low blood tumour burden",
    definition: ">5% of peripheral blood lymphocytes are atypical (Sézary) cells, without meeting B2 criteria.",
    note: "Circulating malignant cells are detectable, indicating that the disease extends beyond the skin."
  },
  B2: {
    name: "High blood tumour burden",
    definition: "≥1,000/µL Sézary cells with a positive clone.",
    note: "Together with erythroderma, B2 blood involvement defines Sézary syndrome."
  }
};
export function P4WhichPatients() {
  const {
    explore
  } = useSnak();
  const [level, setLevel] = useState<BLevel>("B0");
  const info = LEVELS[level];
  return <Screen overline={<>
          Previously treated MF / SS<Ref n={5} data-id="element-478" data-mp="rnmdkdw1w7on" />
        </>} refTarget={5} footnote={<p data-id="element-479" data-mp="nn2gtbfe3631">
          Blood classification per ISCL/EORTC (2007).<Ref n={5} data-id="element-480" data-mp="11limr05fgsk4" /> Updated consensus criteria refine blood assessment,
          including flow cytometry; confirm locally applicable definitions. Conceptual blood film: cell proportions are
          illustrative, not diagnostic thresholds. No relationship between B category and MINT outcomes is shown or implied.
        </p>} data-id="element-477" data-mp="1ff9npk53f448">
      <Reveal delay={0.1} data-id="element-481" data-mp="280szc2tly5jq">
        <h1 className="mt-5 font-display text-[32px] font-medium leading-[1.1] tracking-[-0.01em]" data-id="element-482" data-mp="283oy1tda4y98">
          Blood involvement changes the clinical picture.
        </h1>
      </Reveal>

      <Reveal delay={0.2} data-id="element-483" data-mp="2k2asr5mst2bw">
        <div className="mt-6 flex justify-center" data-id="element-484" data-mp="2k5qakg7uwph0">
          <Segmented size="lg" options={[{
          value: "B0",
          label: "B0"
        }, {
          value: "B1",
          label: "B1"
        }, {
          value: "B2",
          label: "B2"
        }]} value={level} onChange={v => {
          setLevel(v as BLevel);
          explore();
        }} data-id="element-485" data-mp="2p4030j275byx" />
        </div>
        <div className="mx-auto mt-5 w-full max-w-[260px]" data-id="element-486" data-mp="26byzh3eh8xj6">
          <BloodField level={level} data-id="element-487" data-mp="26ae5bew2rglu" />
        </div>
      </Reveal>

      <div className="mt-5 min-h-[170px]" data-id="element-488" data-mp="2b3z5ndl54mo7">
        <AnimatePresence mode="popLayout" initial={false} data-id="element-489" data-mp="2b50jfptjjn9m">
          <motion.div key={level} initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-490" data-mp="1dn0fe0qsnlb8">
            <div className="flex items-baseline gap-3" data-id="element-491" data-mp="164amdjsai2y3">
              <span className="font-display text-[52px] font-medium leading-none text-[var(--s-accent)]" data-id="element-492" data-mp="164tts6kbd8xv">{level}</span>
              <span className="font-display text-[19px] leading-snug" data-id="element-493" data-mp="117afx7rj2y6g">{info.name}</span>
            </div>
            <p className="mt-3 border-l-2 border-[var(--s-accent)] pl-3 text-[15px] leading-relaxed" data-id="element-494" data-mp="w8h2ffc8r76a">
              {info.definition}
              <Ref n={5} data-id="element-495" data-mp="nd9eu7d3sz2x" />
            </p>
            <p className="mt-3 text-[14px] leading-relaxed text-[var(--s-muted)]" data-id="element-496" data-mp="nchuxtsvcpsh">{info.note}</p>
          </motion.div>
        </AnimatePresence>
      </div>
    </Screen>;
}